import { inject, Injectable } from '@angular/core';

import { Customer } from '../../model/customer.model';
import { ExtendedCustomer } from '../../model/extended-customer.model';
import { FoundCustomer } from '../../model/found-customer.model';
import { GcdmCustomerClient } from './gcdm-customer.client';
import { GcdmCreateCustomerRequestMapper } from './mapper/create-customer-request.mapper';
import { GcdmCustomerViewModelMapper } from './mapper/customer-model.mapper';
import { GcdmExtendedCustomerViewModelMapper } from './mapper/extended-customer-model.mapper';
import { GcdmFoundCustomerViewModelMapper } from './mapper/found-customer-model.mapper';
import { GcdmCustomerQueryOptions } from './model/request.model';

/** * A search hit in both shapes the profile sync dialog needs: the list representation and the * comparison representation. Both come from the same business partner, so selecting a result * costs no extra request. */
export interface GcdmCustomerSearchMatch {
  foundCustomer: FoundCustomer;
  extendedCustomer: ExtendedCustomer;
}

/**
 * Searches, creates and updates customers in the NSC Database (GCDM).
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmCustomerService {
  private readonly client = inject(GcdmCustomerClient);
  private readonly foundCustomerMapper = inject(GcdmFoundCustomerViewModelMapper);
  private readonly createCustomerMapper = inject(GcdmCreateCustomerRequestMapper);
  private readonly extendedCustomerMapper = inject(GcdmExtendedCustomerViewModelMapper);
  private readonly customerMapper = inject(GcdmCustomerViewModelMapper);

  async searchCustomer(searchParams: GcdmCustomerQueryOptions): Promise<FoundCustomer[]> {
    const businessPartnersResponse = await this.client.searchCustomer(searchParams);
    return this.foundCustomerMapper.mapToFoundCustomers(businessPartnersResponse);
  }

  /** Creates a brand-new customer (case A1) and returns the GCID assigned by GCDM. */
  async createCustomer(customer: Customer): Promise<string> {
    const response = await this.client.createCustomer(this.createCustomerMapper.mapCustomer(customer));
    return response.gcid;
  }

  /** Creates a customer that already exists locally in Retail CRM (case B1). */
  async createCustomerFromExtendedCustomer(customer: ExtendedCustomer): Promise<string> {
    const response = await this.client.createCustomer(this.createCustomerMapper.mapExtendedCustomer(customer));
    return response.gcid;
  }

  /**
   * Writes the merged customer back (CUSTAPP-611 - case B2) and returns the GCID, echoed by GCDM.
   *
   * Takes the same mapper as the create path, so both directions stay in sync: the backend
   * accepts the identical payload for POST and PUT.
   */
  async updateCustomer(gcid: string, customer: ExtendedCustomer): Promise<string> {
    const response = await this.client.updateCustomer(gcid, this.createCustomerMapper.mapExtendedCustomer(customer));
    return response.gcid;
  }

  /** Writes the edited master data of an already-synced customer back to the NSC Database
   * (CUSTAPP-613) and returns the GCID, echoed by GCDM.
   * The counterpart of {@link updateCustomer}, which serves the sync dialog: that one merges a
   *local record into an NSC one and works on `ExtendedCustomer`, this one edits a record that is
   *already linked and works on the `Customer` the profile page holds.
   */

  async updateSyncedCustomer(gcid: string, customer: Customer): Promise<string> {
    const response = await this.client.updateCustomer(gcid, this.createCustomerMapper.mapCustomerForUpdate(customer));
    return response.gcid;
  }

  /** Searches the NSC Database for the profile sync dialog (CUSTAPP-611, cases B1/B2). */
  async searchCustomersForSync(searchParams: GcdmCustomerQueryOptions): Promise<GcdmCustomerSearchMatch[]> {
    const businessPartners = await this.client.searchCustomer(searchParams);
    return businessPartners.map((businessPartner) => ({
      foundCustomer: this.foundCustomerMapper.mapToFoundCustomer(businessPartner),
      extendedCustomer: this.extendedCustomerMapper.mapToExtendedCustomer(businessPartner),
    }));
  }

  /** Reads the master data of a synced customer from the NSC Database and overlays it onto the
   * Retail CRM record, which keeps the fields GCDM does not own (CUSTAPP-613 read side).
   */
  async getSyncedCustomer(gcid: string, retailCustomer: Customer): Promise<Customer> {
    const businessPartner = await this.client.getCustomerByGcid(gcid);
    return this.customerMapper.mapToCustomer(businessPartner, retailCustomer);
  }
}
